import {
  BufferGeometry,
  CanvasTexture,
  Color,
  Line,
  LinearFilter,
  LinearMipMapLinearFilter,
  LineBasicMaterial,
  Matrix4,
  Object3D,
  Sprite,
  SpriteMaterial,
  Vector3,
} from "three";

class SpritePlane extends Object3D {
  constructor(size: number) {
    super();
  }
}

export class SimpleMeter extends Object3D {
  private readonly _line: Line;
  private readonly _labelSprite: Sprite;
  private readonly _screenSize = 0.025;
  private _aspectRatio = 1;
  private _zoom = 0;
  private _size = 0;
  private _direction = new Vector3(1, 0, 0);

  get size() {
    return this._size;
  }

  get direction() {
    return this._direction;
  }

  private _attachTo?: Object3D;
  get attachTo() {
    return this._attachTo;
  }

  get labelSprite() {
    return this._labelSprite;
  }

  constructor(size: number, attachTo?: Object3D) {
    super();
    this._attachTo = attachTo;
    this._size = size;
    const points = [new Vector3(), new Vector3(0, 0, size)];
    const geometry = new BufferGeometry().setFromPoints(points);
    const material = new LineBasicMaterial({
      color: 0x000000,
      linewidth: 20,
    });

    this._line = new Line(geometry, material);
    this.add(this._line);

    this._labelSprite = createSprite(0, "cm");
    this.add(this._labelSprite);
  }

  setSizeLabel(size: number) {
    this._labelSprite.material?.map?.dispose();
    const { data, width, height } = createTexture(
      `${(size * 100).toFixed(0)} cm`,
    );
    this._aspectRatio = width / height;
    this._labelSprite.material.map = data;
    this._labelSprite.scale.set(
      this._zoom * this._screenSize * this._aspectRatio,
      this._zoom * this._screenSize,
      1,
    );
  }

  setSize(size: number) {
    this._line?.geometry?.dispose();
    this._line.geometry = new BufferGeometry().setFromPoints([
      new Vector3(),
      new Vector3(0, 0, size),
    ]);

    this._labelSprite.material?.map?.dispose();
    const { data, width, height } = createTexture(
      `${(size * 100).toFixed(0)} cm`,
    );

    this._aspectRatio = width / height;

    this._labelSprite.material.map = data;
    this._labelSprite.position.setZ(size * 0.5);
    this._labelSprite.scale.set(
      this._zoom * this._screenSize * this._aspectRatio,
      this._zoom * this._screenSize,
      1,
    );
  }

  setZoom(zoom: number) {
    this._zoom = zoom;
    this._labelSprite.scale.set(
      this._zoom * this._screenSize * this._aspectRatio,
      this._zoom * this._screenSize,
      1,
    );
  }

  setDirection(direction: Vector3, up: Vector3 = new Vector3(0, 1, 0)) {
    this._direction = direction.clone().normalize();
    const z = direction.clone().normalize();
    const x = new Vector3().crossVectors(up, z).normalize();
    const y = new Vector3().crossVectors(z, x).normalize();
    const m = new Matrix4().makeBasis(x, y, z);
    this.quaternion.setFromRotationMatrix(m);
  }

  updateScale(zoom: number) {
    //this._labelSprite.scale.set(1, 1, zoom);
  }

  dispose() {
    this._labelSprite.material?.map?.dispose();
    this._line.geometry.dispose();
  }
}

export const createSprite = (
  lenght: number,
  unit = "cm",
  upVector = new Vector3(0, 1, 0),
  overridenLength?: number,
) => {
  const pos = upVector.clone().multiplyScalar(0.5 * lenght);
  const len = overridenLength ? overridenLength : lenght;
  let text;

  if (unit === "mm") {
    text = (len * 1000).toFixed(0) + " mm";
  } else if (unit === "cm") {
    const num = len * 100;
    const exp = num % 1;
    if (exp > 0.01) {
      text = num.toFixed(1) + " cm";
    } else {
      text = num.toFixed(0) + " cm";
    }
  } else {
    text = len.toFixed(2) + " m";
  }
  return createSpriteMeasure(text, pos, 0);
};

export const createSpriteMeasure = (
  text: string,
  pos: Vector3,
  angle: number,
) => {
  const { data, width, height } = createTexture(text, 0, 0, 10, 10);
  const spriteMaterial = new SpriteMaterial({
    color: new Color(0xffffff),
    transparent: true,
    depthWrite: false,
    depthTest: false,
    map: data,
  });

  const spriteObject = new Sprite();

  spriteObject.scale.set((1 * width) / height, 1, 1);
  spriteObject.position.set(pos.x, pos.y, pos.z);

  spriteObject.name = "Sprite";
  spriteObject.renderOrder = -999;
  spriteObject.material = spriteMaterial;
  spriteObject.userData.contributeToAO = "false";
  return spriteObject;
};

export const createTexture = (text: string, x = 0, y = 0, h = 10, s = 10) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { alpha: true })!;

  const hp = h * 0.2 * s;
  const wp = h * 0.2 * s;

  const tmpCanvas = document.createElement("canvas");
  const tmpCtx = tmpCanvas.getContext("2d")!;
  const hf = h * s * 0.7;
  tmpCtx.font = `${hf}px Arial`;
  const a = tmpCtx.measureText(text);
  const textWidth = a.width;
  const textHeight = a.actualBoundingBoxAscent + a.actualBoundingBoxDescent;

  const w1 = textWidth + wp * 2;
  const h1 = textHeight + hp * 2;

  const w2 = Math.max(Math.pow(2, Math.ceil(Math.log2(w1))), 16);
  const h2 = Math.max(Math.pow(2, Math.ceil(Math.log2(h1))), 16);

  canvas.width = w2;
  canvas.height = h2;

  ctx.fillStyle = "#000000FF";
  const arc = Math.min(w1, h1) / 4;
  ctx.beginPath();
  (ctx as any).roundRect((w2 - w1) / 2, (h2 - h1) / 2, w1, h1, arc);
  ctx.fill();

  ctx.font = `${hf}px Arial`;
  ctx.fillStyle = "#FFFFFFFF";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const textX = w2 / 2;
  const textY = h2 / 2;
  ctx.fillText(text, textX, textY);

  const texture = new CanvasTexture(canvas);
  texture.minFilter = LinearMipMapLinearFilter;
  texture.magFilter = LinearFilter;
  texture.anisotropy = 2;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;

  return {
    data: texture,
    width: w2,
    height: h2,
  };
};
